<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { useDialog } from '@/shared/composables/useDialog';
import SharesAllotmentForm from './SharesAllotmentForm.vue';

// Dialog 초기화
const { warning: showWarning } = useDialog();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료 상태)
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false
});

// Emits
const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string }];
}>();

// SharesAllotmentForm ref
const formRef = ref<InstanceType<typeof SharesAllotmentForm> | null>(null);

// 상태
const isSubmitting = ref(false);
const isValidating = ref(false);

// 검증 결과
const validationResult = ref<any>(null);

// 필드명을 사용자 친화적으로 변환
const formatFieldLabel = (field: any): string => {
  let label = field.label || field.field || '';
  if (label.endsWith(' Code') || label.endsWith(' Name')) {
    label = label.replace(/ Code$/, '').replace(/ Name$/, '');
  }
  return label;
};

// 필드별 validation 에러 매핑 (SharesAllotmentForm에 전달)
const fieldValidationErrors = computed(() => {
  const fields = validationResult.value?.data?.missingFields || [];
  if (!fields.length) return {};

  const errors: Record<string, string> = {};
  
  fields.forEach((field: any) => {
    const fieldPath = field.field || '';
    const label = formatFieldLabel(field);
    
    const mappedField = mapFieldPathToComponentField(fieldPath);
    if (mappedField) {
      errors[mappedField] = t('startTask.This field is required') || `${label} is required`;
    }
  });

  return errors;
});

// 백엔드 필드 경로를 컴포넌트 필드명으로 매핑
const mapFieldPathToComponentField = (fieldPath: string): string | null => {
  const fieldMap: Record<string, string> = {
    'transactionDate': 'transactionDate',
    'numberOfShares': 'numberOfShares',
    'contactName': 'shareholderName',
    'contactEmail': 'shareholderEmail',
    'category.code': 'category',
    'category.name': 'category',
    'idType.code': 'idType',
    'idType.name': 'idType',
    'nationality.code': 'nationality',
    'nationality.name': 'nationality',
    'currency.code': 'currency',
    'currency.name': 'currency',
    'classOfShares.code': 'classOfShares',
    'classOfShares.name': 'classOfShares',
    'entityUEN': 'entityUEN',
    'filingAgentUEN': 'filingAgentUEN',
  };

  if (fieldMap[fieldPath]) {
    return fieldMap[fieldPath];
  }

  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    const baseField = pathParts[0];
    if (fieldMap[fieldPath]) {
      return fieldMap[fieldPath];
    }
    if (fieldMap[baseField]) {
      return fieldMap[baseField];
    }
  }

  return null;
};

// 최종 전송용 데이터 (eFilingDataForApi)
const finalEFilingData = ref<any>(null);

// Entity 정보 (taskData 또는 finalEFilingData에서 추출)
const entityUEN = computed(() => {
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.EntityUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.EntityUEN;
  const fromTaskData = props.taskData?.entityUEN;
  const fromFinalData = finalEFilingData.value?.EntityUEN;
  const fromAllotList = props.taskData?.eFilingDataForApi?.AllotList?.[0]?.EntityUEN ||
                        props.taskData?.eFilingDataValidation?.AllotList?.[0]?.EntityUEN ||
                        finalEFilingData.value?.AllotList?.[0]?.EntityUEN;

  const result = fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || fromFinalData || fromAllotList || '';
  return result;
});

const filingAgentUEN = computed(() => {
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.FilingAgentUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.FilingAgentUEN;
  const fromTaskData = props.taskData?.filingAgentUEN;
  const fromFinalData = finalEFilingData.value?.FilingAgentUEN;

  return fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || fromFinalData || entityUEN.value;
});

const entityName = computed(() => {
  return props.taskData?.eFilingData?.entityName?.name ||
         props.taskData?.eFilingData?.entityName ||
         props.taskData?.eFilingDataValidation?.entityName?.name ||
         props.taskData?.eFilingDataValidation?.entityName ||
         props.taskData?.eFilingDataForApi?.entityName ||
         props.taskData?.entityName ||
         finalEFilingData.value?.entityName ||
         '';
});

// eFilingDataForApi의 ShareholderList를 formData.shareholders 구조로 변환
const initialData = computed(() => {
  if (!props.taskData) return null;
  
  const formData = props.taskData.formData || {};
  const shareholderListForApi = props.taskData.eFilingDataForApi?.ShareholderList || [];
  
  // 값 추출 헬퍼 함수 (객체인 경우 value 속성 사용)
  const getValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && 'value' in val) return String(val.value || '');
    return String(val);
  };
  
  // eFilingDataForApi가 있으면 그것을 우선 사용, 없으면 formData 사용
  // formData.shareholders가 있으면 eFilingDataForApi의 값으로 업데이트
  if (formData.shareholders && Array.isArray(formData.shareholders)) {
    const updatedShareholders = formData.shareholders.map((sh: any, index: number) => {
      // Deep copy로 새 객체 생성 (중첩된 객체 참조 문제 해결)
      const updatedSh = JSON.parse(JSON.stringify(sh));
      
      // eFilingDataForApi가 있고 해당 인덱스의 항목이 있으면 그것을 우선 사용
      if (shareholderListForApi.length > 0 && shareholderListForApi[index]) {
        const shForApi = shareholderListForApi[index];
        
        if (updatedSh.currencies?.[0]?.shareClasses?.[0]) {
          const shareClass = updatedSh.currencies[0].shareClasses[0];
          
          // eFilingDataForApi의 값이 있으면 우선 사용 (빈 문자열이 아닌 경우)
          // 빈 문자열이면 formData의 값을 유지
          if (shForApi.numberOfShares !== undefined && 
              shForApi.numberOfShares !== null && 
              String(shForApi.numberOfShares).trim() !== '') {
            shareClass.numberOfShares = String(shForApi.numberOfShares);
          }
          if (shForApi.issuedShareCapital !== undefined && 
              shForApi.issuedShareCapital !== null && 
              String(shForApi.issuedShareCapital).trim() !== '') {
            shareClass.issuedShareCapital = String(shForApi.issuedShareCapital);
          }
          if (shForApi.paidUpShareCapital !== undefined && 
              shForApi.paidUpShareCapital !== null && 
              String(shForApi.paidUpShareCapital).trim() !== '') {
            shareClass.paidUpShareCapital = String(shForApi.paidUpShareCapital);
          }
        }
      } else {
        // eFilingDataForApi가 없으면 formData의 값을 사용하되, 객체인 경우 value 추출
        if (updatedSh.currencies?.[0]?.shareClasses?.[0]) {
          const shareClass = updatedSh.currencies[0].shareClasses[0];
          
          // formData의 값이 객체 형태일 수 있으므로 value 추출
          shareClass.numberOfShares = getValue(shareClass.numberOfShares);
          shareClass.issuedShareCapital = getValue(shareClass.issuedShareCapital);
          shareClass.paidUpShareCapital = getValue(shareClass.paidUpShareCapital);
        }
      }
      
      return updatedSh;
    });
    
    return {
      formData: {
        ...formData,
        shareholders: updatedShareholders,
        transactionDate: shareholderListForApi.length > 0 && shareholderListForApi[0]?.transactionDate
          ? shareholderListForApi[0].transactionDate
          : formData.transactionDate || ''
      }
    };
  }
  
  // formData.shareholders가 없으면 eFilingDataForApi에서 생성
  if (shareholderListForApi.length > 0) {
    const shareholders = shareholderListForApi.map((sh: any) => {
      return {
        shareholder: sh.contactCd || sh.ContactCd || '',
        transactionDate: sh.transactionDate || '',
        currencies: [{
          currency: sh.currency || sh.currencyCd || '',
          sharesPayable: sh.sharesPayable || 'all-in-cash',
          shareClasses: [{
            classOfShares: sh.classOfShares || sh.classOfSharesCd || '',
            numberOfShares: sh.numberOfShares || '',
            issuedShareCapital: sh.issuedShareCapital || '',
            paidUpShareCapital: sh.paidUpShareCapital || ''
          }]
        }]
      };
    });
    
    return {
      formData: {
        ...formData,
        shareholders: shareholders,
        transactionDate: shareholderListForApi[0]?.transactionDate || formData.transactionDate || ''
      }
    };
  }
  
  return { formData };
});

// 뒤로가기
const handleBack = () => {
  emit('back');
};

// Deep merge 함수 - 빈 값은 기존 값 유지
const deepMerge = (base: any, update: any): any => {
  if (!base) return update;
  if (!update) return base;

  if (typeof base !== 'object' || typeof update !== 'object') {
    if (update === '' || update === null || update === undefined) {
      return base;
    }
    return update;
  }

  if (Array.isArray(base) && Array.isArray(update)) {
    const maxLen = Math.max(base.length, update.length);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < update.length && i < base.length) {
        result.push(deepMerge(base[i], update[i]));
      } else if (i < base.length) {
        result.push(base[i]);
      } else {
        result.push(update[i]);
      }
    }
    return result;
  }

  const result = { ...base };
  for (const key of Object.keys(update)) {
    const updateValue = update[key];
    const baseValue = base[key];

    if (typeof updateValue === 'object' && updateValue !== null &&
        'code' in updateValue && 'name' in updateValue) {
      if (!updateValue.code && !updateValue.name) {
        result[key] = baseValue || updateValue;
      } else if (!updateValue.code && baseValue?.code) {
        result[key] = { ...updateValue, code: baseValue.code };
      } else if (!updateValue.name && baseValue?.name) {
        result[key] = { ...updateValue, name: baseValue.name };
      } else {
        result[key] = deepMerge(baseValue, updateValue);
      }
    } else {
      result[key] = deepMerge(baseValue, updateValue);
    }
  }

  return result;
};

// task_data 기반으로 eFilingData 업데이트 (deep merge)
const mergeTaskDataWithEFiling = (baseTaskData: any, newEFilingData: { eFilingDataValidation: any; eFilingDataForApi: any }): any => {
  const merged = JSON.parse(JSON.stringify(baseTaskData || {}));

  if (merged.eFilingDataValidation) {
    merged.eFilingDataValidation = deepMerge(merged.eFilingDataValidation, newEFilingData.eFilingDataValidation);
  } else {
    merged.eFilingDataValidation = newEFilingData.eFilingDataValidation;
  }

  if (merged.eFilingDataForApi) {
    merged.eFilingDataForApi = deepMerge(merged.eFilingDataForApi, newEFilingData.eFilingDataForApi);
  } else {
    merged.eFilingDataForApi = newEFilingData.eFilingDataForApi;
  }

  return merged;
};

// 디버깅용 데이터
const debugData = ref<{
  originalEFilingData: any;
  mergedEFilingData: any;
  mergedEFilingDataForApi: any;
  changes: Array<{ path: string; oldValue: any; newValue: any }>;
}>({
  originalEFilingData: null,
  mergedEFilingData: null,
  mergedEFilingDataForApi: null,
  changes: []
});

// Submit - E-Filing 데이터 생성 후 검증 및 저장
const handleSubmit = async () => {
  console.log('📋 [SharesAllotmentEFiling] Submit 클릭');

  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [SharesAllotmentEFiling] Form ref가 없습니다');
      return;
    }

    // SharesAllotmentForm의 getEFilingData 호출
    const updatedEFilingData = await formRef.value.getEFilingData?.();
    console.log('📋 [SharesAllotmentEFiling] Updated E-Filing 데이터:', updatedEFilingData);

    // task_data 기반으로 eFilingData 업데이트
    const mergedData = mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    console.log('📋 [SharesAllotmentEFiling] Merged 데이터:', mergedData);

    const originalEFilingDataValidation = props.taskData?.eFilingDataValidation || null;
    const mergedEFilingDataValidation = mergedData.eFilingDataValidation || null;
    const mergedEFilingDataForApi = mergedData.eFilingDataForApi || null;

    debugData.value = {
      originalEFilingData: originalEFilingDataValidation,
      mergedEFilingData: mergedEFilingDataValidation,
      mergedEFilingDataForApi: mergedEFilingDataForApi,
      changes: []
    };

    // 바로 검증 및 저장 실행
    await handleValidateAndSubmit();
  } finally {
    isSubmitting.value = false;
  }
};

// 디버깅 다이얼로그에서 OK 클릭 - eFilingData 저장 후 /validate/:taskId 호출
const handleValidateAndSubmit = async () => {
  console.log('📋 [SharesAllotmentEFiling] Save & Validate 호출');

  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      console.error('❌ [SharesAllotmentEFiling] taskId가 없습니다');
      validationResult.value = {
        success: false,
        error: 'Task ID is required'
      };
      return;
    }

    // 1. eFilingDataValidation + eFilingDataForApi 저장
    console.log('📤 [SharesAllotmentEFiling] eFilingData 저장 중...');
    const saveResponse = await apiClient.put(
      `/startTask/shares-allotment/update-efiling-data/${props.taskId}`,
      {
        eFilingDataValidation: debugData.value.mergedEFilingData,
        eFilingDataForApi: debugData.value.mergedEFilingDataForApi
      }
    );
    console.log('✅ [SharesAllotmentEFiling] eFilingData 저장 완료:', saveResponse);

    // 2. /validate/:taskId API 호출
    console.log('📤 [SharesAllotmentEFiling] Validate 호출 중...');
    const response = await apiClient.get(`/startTask/shares-allotment/validate/${props.taskId}`);
    console.log('✅ [SharesAllotmentEFiling] Validate 응답:', response);

    validationResult.value = {
      success: true,
      data: response?.data || response
    };

    // 검증 성공 시 최종 전송용 데이터 사용
    if (validationResult.value?.data?.isValid) {
      finalEFilingData.value = debugData.value.mergedEFilingDataForApi;
      console.log('📋 [SharesAllotmentEFiling] 최종 API용 데이터:', finalEFilingData.value);

      // 검증 성공 시 부모 컴포넌트(EFilingPage)로 데이터 전달하여 Singpass 모달 호출
      emit('validation-success', {
        eFilingData: finalEFilingData.value,
        entityUEN: entityUEN.value,
        filingAgentUEN: filingAgentUEN.value,
        entityName: entityName.value
      });
    } else {
      // 검증 실패 시 알림창 표시
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') || 'Please check the required fields and enter the values.'
      );
    }
  } catch (error: any) {
    console.error('❌ [SharesAllotmentEFiling] Save & Validate 실패:', error);
    validationResult.value = {
      success: false,
      error: error.message || 'Save or Validation failed'
    };
  } finally {
    isValidating.value = false;
  }
};

// Form ref를 외부에서 접근 가능하도록 expose
defineExpose({
  formRef,
  getEFilingData: async () => {
    if (formRef.value) {
      return await formRef.value.getEFilingData?.();
    }
    return null;
  },
  getMergedData: async () => {
    if (formRef.value) {
      const updatedEFilingData = await formRef.value.getEFilingData?.();
      return mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    }
    return null;
  }
});
</script>

<template>
  <v-card rounded="md" class="shares-allotment-efiling">
    <v-card-text>
      <SharesAllotmentForm
        ref="formRef"
        :initial-data="initialData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="true"
        :validation-errors="fieldValidationErrors"
      />
    </v-card-text>
    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4 pr-8">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating"
        :disabled="isSubmitting || isValidating"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>
    <!-- 읽기 전용 모드일 때 완료 상태 표시 -->
    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.shares-allotment-efiling {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.shares-allotment-efiling :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
}

.shares-allotment-efiling :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.shares-allotment-efiling :deep(.v-col) {
  padding-left: 8px;
  padding-right: 8px;
}

.shares-allotment-efiling :deep(.v-card-actions) {
  padding-right: 32px !important;
}
</style>
